import { Check } from 'lucide-react';
import { type KeyboardEvent, type MouseEvent, type ReactNode, useCallback, useId, useRef, useState } from 'react';
import { cn } from '../../lib/cn';
import { Kbd } from './Badge';
import { type Placement, pointRect, type Rect, rectOf } from './layer';
import { Popover } from './Popover';

export type MenuItem =
  | {
      type?: 'item';
      key: string;
      label: string;
      icon?: ReactNode;
      shortcut?: string;
      description?: string;
      onSelect: () => void;
      disabled?: boolean;
      tone?: 'danger';
    }
  | { type: 'radio'; key: string; label: string; icon?: ReactNode; checked: boolean; onSelect: () => void }
  | { type: 'separator'; key: string }
  | { type: 'label'; key: string; label: string };

type MenuProps = {
  open: boolean;
  onClose: () => void;
  anchor: HTMLElement | Rect | null;
  items: MenuItem[];
  placement?: Placement;
  label: string;
  className?: string;
};

/** Спливне меню (role="menu"): стрілки, Home/End, пошук за першою літерою, Enter/Пробіл, Esc. */
export function Menu({ open, onClose, anchor, items, placement = 'bottom-start', label, className }: MenuProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const id = useId();

  const focusable = () => Array.from(listRef.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]:not([aria-disabled="true"])') ?? []);
  const move = (delta: number | 'first' | 'last') => {
    const list = focusable();
    if (!list.length) return;
    const idx = list.indexOf(document.activeElement as HTMLElement);
    let next = 0;
    if (delta === 'first') next = 0;
    else if (delta === 'last') next = list.length - 1;
    else next = (idx + delta + list.length) % list.length;
    list[next]?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      move(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      move(-1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      move('first');
    } else if (e.key === 'End') {
      e.preventDefault();
      move('last');
    } else if (e.key === 'Tab') {
      onClose();
    } else if (e.key.length === 1 && /\S/.test(e.key) && !e.metaKey && !e.ctrlKey) {
      const list = focusable();
      const ch = e.key.toLocaleLowerCase('uk-UA');
      const start = list.indexOf(document.activeElement as HTMLElement) + 1;
      const ordered = [...list.slice(start), ...list.slice(0, start)];
      ordered.find((el) => (el.textContent ?? '').trim().toLocaleLowerCase('uk-UA').startsWith(ch))?.focus();
    }
  };

  const select = (fn: () => void) => {
    onClose();
    // Дія після закриття меню — щоб діалоги відкривалися поверх, а фокус повертався правильно.
    window.setTimeout(fn, 0);
  };

  return (
    <Popover open={open} onClose={onClose} anchor={anchor} placement={placement} role="presentation" initialFocus="first" className={cn('min-w-[220px] p-1', className)}>
      <div ref={listRef} id={id} role="menu" aria-label={label} onKeyDown={onKeyDown} className="flex flex-col outline-none">
        {items.map((item) => {
          if (item.type === 'separator') return <div key={item.key} role="separator" className="my-1 h-px bg-line" />;
          if (item.type === 'label')
            return (
              <div key={item.key} className="px-2.5 pt-2 pb-1 text-caption font-medium text-fg-3">
                {item.label}
              </div>
            );
          if (item.type === 'radio')
            return (
              <button
                key={item.key}
                type="button"
                role="menuitemradio"
                aria-checked={item.checked}
                onClick={() => select(item.onSelect)}
                className="flex h-8 items-center gap-2.5 rounded-sm px-2.5 text-left text-body text-fg outline-none hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-none"
              >
                <span className="flex size-4 items-center justify-center text-fg-3">{item.icon}</span>
                <span className="flex-1">{item.label}</span>
                {item.checked && <Check className="size-4 text-accent-fg" aria-hidden />}
              </button>
            );
          return (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              aria-disabled={item.disabled || undefined}
              onClick={() => !item.disabled && select(item.onSelect)}
              className={cn(
                'flex min-h-8 items-center gap-2.5 rounded-sm px-2.5 py-1 text-left text-body outline-none',
                'hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-none',
                item.tone === 'danger' ? 'text-danger-fg' : 'text-fg',
                item.disabled && 'cursor-not-allowed opacity-50 hover:bg-transparent',
              )}
            >
              {item.icon && <span className={cn('flex size-4 shrink-0 items-center justify-center', item.tone === 'danger' ? 'text-danger-fg' : 'text-fg-3')}>{item.icon}</span>}
              <span className="min-w-0 flex-1">
                <span className="block truncate">{item.label}</span>
                {item.description && <span className="block text-caption text-fg-3">{item.description}</span>}
              </span>
              {item.shortcut && <Kbd className="ml-3">{item.shortcut}</Kbd>}
            </button>
          );
        })}
      </div>
    </Popover>
  );
}

/** Керування меню з кнопкою-тригером. */
export function useMenu() {
  const [anchor, setAnchor] = useState<HTMLElement | Rect | null>(null);
  const open = anchor !== null;
  const close = useCallback(() => setAnchor(null), []);
  const triggerProps = {
    'aria-haspopup': 'menu' as const,
    'aria-expanded': open,
    onClick: (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      const el = e.currentTarget;
      setAnchor((a) => (a ? null : el));
    },
  };
  /** Контекстне меню в точці курсора; з клавіатури (Shift+F10) — біля елемента. */
  const onContextMenu = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.clientX === 0 && e.clientY === 0) setAnchor(rectOf(e.currentTarget));
    else setAnchor(pointRect(e.clientX, e.clientY));
  };
  const openAt = (target: HTMLElement | Rect) => setAnchor(target);
  return { open, anchor, close, triggerProps, onContextMenu, openAt };
}
